import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, Store, Zap, Globe, BarChart3, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";

const PARTNER_IMG = "https://images.pexels.com/photos/3855473/pexels-photo-3855473.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export function PartnerLanding() {
  const { t } = useI18n();
  const [form, setForm] = useState({ company: "", contact_name: "", email: "", phone: "", country: "SK", website: "", catalog_size: "", feed_type: "API", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/partners/apply", form);
      setSent(true);
      toast.success(t("partnerform.success"));
    } catch { toast.error("Something went wrong"); }
    finally { setBusy(false); }
  };

  const benefits = [
    [Globe, t("partner.b1")], [Zap, t("partner.b2")], [BarChart3, t("partner.b3")],
  ];

  return (
    <div>
      <div className="border-b border-border bg-slate-900">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:items-center lg:px-8">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-blue-300"><Store className="h-4 w-4" />{t("nav.partners")}</span>
            <h1 className="mt-6 font-heading text-3xl font-bold leading-tight tracking-tighter text-white sm:text-4xl lg:text-5xl">{t("partner.title")}</h1>
            <p className="mt-4 max-w-md text-slate-300">{t("partner.subtitle")}</p>
            <ul className="mt-6 space-y-3">
              {benefits.map(([Icon, b]) => (
                <li key={b} className="flex items-center gap-3 text-sm text-slate-200"><Icon className="h-5 w-5 text-emerald-400" />{b}</li>
              ))}
            </ul>
          </div>
          <div className="overflow-hidden rounded-2xl border border-slate-700"><img src={PARTNER_IMG} alt="partner" className="h-full w-full object-cover" /></div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{t("partnerform.title")}</h2>
        <p className="mt-2 text-slate-600">{t("partnerform.subtitle")}</p>

        {sent ? (
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="mt-8 rounded-lg border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="partner-success">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-500 text-white"><Check className="h-7 w-7" /></div>
            <p className="mt-4 font-heading text-lg font-semibold text-slate-900">{t("partnerform.success")}</p>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="mt-8 grid gap-4 rounded-lg border border-border bg-white p-8 sm:grid-cols-2">
            {[["company", t("partnerform.company")], ["contact_name", t("partnerform.contact")], ["email", t("auth.email")], ["phone", t("partnerform.phone")], ["website", t("partnerform.website")], ["catalog_size", t("partnerform.catalog")]].map(([k, label]) => (
              <div key={k} className={k === "message" ? "sm:col-span-2" : ""}>
                <label className="text-sm font-medium text-slate-700">{label}</label>
                <input data-testid={`partner-${k}`} required={["company", "contact_name", "email"].includes(k)} type={k === "email" ? "email" : "text"}
                  value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="mt-1.5 w-full rounded-lg border border-border px-4 py-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
            ))}
            <div>
              <label className="text-sm font-medium text-slate-700">{t("product.country")}</label>
              <select data-testid="partner-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="mt-1.5 w-full rounded-lg border border-border px-4 py-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary">
                {["SK", "CZ", "PL", "HU"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">{t("partnerform.feed")}</label>
              <select data-testid="partner-feed" value={form.feed_type} onChange={(e) => setForm({ ...form, feed_type: e.target.value })}
                className="mt-1.5 w-full rounded-lg border border-border px-4 py-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary">
                {["API", "XML", "CSV"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-slate-700">{t("partnerform.message")}</label>
              <textarea data-testid="partner-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-1.5 w-full rounded-lg border border-border px-4 py-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary" />
            </div>
            <button type="submit" data-testid="partner-submit" disabled={busy} className="rounded-full bg-primary px-6 py-3 font-heading font-semibold text-white transition-colors duration-200 hover:bg-blue-600 disabled:opacity-60 sm:col-span-2">
              {busy ? t("common.loading") : t("partnerform.submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export function PartnerDashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  useEffect(() => { api.get("/partners/mine").then(({ data }) => setApps(data)).catch(() => {}); }, []);

  const stats = [
    ["Clicks (30d)", "1,284"], ["Impressions", "48,900"], ["Active offers", "312"], ["Conversion", "4.2%"],
  ];

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{t("nav.dashboard")}</h1>
      <p className="mt-2 text-slate-600">{user?.name} · {user?.email}</p>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map(([label, val]) => (
          <div key={label} className="rounded-lg border border-border bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</div>
            <div className="mt-2 font-heading text-2xl font-bold tracking-tight text-slate-900">{val}</div>
          </div>
        ))}
      </div>

      <h2 className="mt-10 font-heading text-xl font-semibold tracking-tight text-slate-900">Applications</h2>
      <div className="mt-4 rounded-lg border border-border bg-white">
        {apps.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="dashboard-empty">
            <FileText className="mx-auto mb-3 h-8 w-8 text-slate-300" />No partner applications linked to your email yet.
          </div>
        ) : apps.map((a) => (
          <div key={a.id} className="flex items-center justify-between border-b border-border p-4 last:border-0">
            <div>
              <div className="font-heading font-semibold tracking-tight text-slate-900">{a.company}</div>
              <div className="text-sm text-slate-500">{a.country} · {a.feed_type} feed</div>
            </div>
            <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold capitalize text-amber-600">{a.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
